<template>
	<div v-if="rowOffers.length > 0 || bundleOffers.length > 0" class="buybox-row" :class="{ promoted, inline }">
		<label v-if="!hideCategoryLabel" class="buybox-row__label" :class="{ 'ttb-language': isTTBLanguage }">
			<slot>{{ $t('WEBAPP_STREAM') }}</slot>
		</label>

		<div class="buybox-row__offers">
			<slot v-for="offer in rowOffers" name="row" v-bind="{ offer }">
				<GridOffer
					:key="`${offer.id}-${offer.monetizationType}-${activePresentationType ?? 'default'}`"
					:offer="offer"
					:promoted="promoted"
					:objectType="title.objectType"
					:title="title"
					:inline="inline"
					:additionalContexts="additionalContexts"
					@clickOut="$emit('clickOut', $event)"
				/>
			</slot>

			<BundleOffer
				v-for="(bundleOffer, index) of bundleOffers"
				:key="`${bundleOffer?.id}_${bundleOffer?.package?.packageId}_${index}`"
				:isBannerBuyBox="isBannerBuyBox"
				:additionalContexts="additionalContexts"
				:offer="bundleOffer"
				:title="title"
				:episodeBuyBoxPresentationType="episodeBuyBoxPresentationType"
				:isEpisodeBuybox="isEpisodeBuybox"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { PromotedBundleEdge } from '@/@types/graphql-types';
import type { BuyboxPresentationType } from '@/stores/pinia/useUserStore';

import { MonetizationType } from '@/@types/graphql-types';

import GridOffer from '@/features/buybox/components/grid/GridOffer.vue';
import BundleOffer from '@/features/buybox/components/grid/BundleOffer.vue';

import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
};

interface BuyBoxRowProps {
	title: TitleDetail;
	promoted?: boolean;
	inline?: boolean;
	hideCategoryLabel?: boolean;
	additionalContexts?: SnowplowContext[];
	offers?: BuyboxRowOffer[];
	bundles?: PromotedBundleEdge[];
	isBannerBuyBox?: boolean;
	// START AVOD
	isAVODActive?: boolean;
	// END AVOD
	episodeBuyBoxPresentationType?: BuyboxPresentationType | null;
	isEpisodeBuybox?: boolean;
	activePresentationType?: BuyboxPresentationType | null;
}

const props = withDefaults(defineProps<BuyBoxRowProps>(), {
	offers: () => [],
	additionalContexts: () => [],
	bundles: () => [],
	isFreeBuyBox: false,
});

const { isTTBLanguage } = storeToRefs(useLanguageStore());

const bundleOffers = computed(() => {
	return props.bundles.map(bundle => {
		return {
			...bundle.offer,
			standardWebURL: bundle.promotionUrl,
			bundleId: bundle.node.bundleId,
			bundleTechnicalName: bundle.node.technicalName,
			icons: bundle.node.packages.map(pkg => pkg.icon),
		};
	});
});

// START AVOD
const rowOffers = computed(() => {
	// Only show One Plex offer at a time.
	// + Always filtering out Justwatch-plex Provider as it's only used by partners.
	const AVODTechnicalName = props.isAVODActive
		? ['plex', 'justwatchplexchannel']
		: ['plexplayer', 'justwatchplexchannel'];

	// HIDE OTT Studio Channel from regular free offers when Player offer is present
	if (props.offers.some(offer => offer.package.technicalName === 'ottstudioplayer')) {
		AVODTechnicalName.push('ottstudiochannel');
	}
	const offers = [...props.offers.filter(el => !AVODTechnicalName.includes(el.package.technicalName))];

	// JUSTWATCH_TV
	const indexOfJustWatchTvOffer = offers.findIndex(offer => offer.package.technicalName === 'justwatchtv');
	const indexOfFirstFreeOffer = offers.findIndex(offer =>
		[MonetizationType.Ads, MonetizationType.Free].includes(offer.monetizationType)
	);
	if (indexOfJustWatchTvOffer > indexOfFirstFreeOffer) {
		const justWatchTVhOffer = offers.splice(indexOfJustWatchTvOffer, 1)[0];
		offers.splice(indexOfFirstFreeOffer, 0, justWatchTVhOffer);
	}
	// JUSTWATCH_TV

	return offers;
});
// END AVOD
</script>

<style scoped lang="scss">
@import '~@/styles/variables';

@layer standardbuybox {
	.buybox-row {
		display: flex;
		gap: 16px;
		align-items: center;

		&.inline {
			flex-direction: column;

			.buybox-row__label {
				transform: none;
				writing-mode: unset;
				min-height: min-content;
				width: 100%;
				padding: 4px 16px;
				height: min-content;
			}
		}

		&:last-of-type:not(.inline) {
			margin-bottom: 16px;
		}

		&__label {
			margin: 0;
			padding: 16px 4px;
			min-height: 90px;
			height: 95px;
			font-size: 14px;
			font-weight: 400;
			line-height: 100%;
			letter-spacing: 0.24px;
			text-transform: uppercase;
			flex-shrink: 0;

			display: flex;
			align-items: center;
			justify-content: center;

			writing-mode: vertical-lr;

			&:not(.ttb-language) {
				transform: rotate(180deg);
			}

			color: $monetization-stream-text;
			background: $monetization-stream-bg;
		}

		&.stream > .buybox-row__label {
			color: $monetization-stream-text;
			background: $monetization-stream-bg;
		}

		&.fast > .buybox-row__label,
		&.rent > .buybox-row__label {
			color: $monetization-rent-text;
			background: $monetization-rent-bg;
		}

		&.buy > .buybox-row__label {
			color: $monetization-buy-text;
			background: $monetization-buy-bg;
		}

		&__offers {
			display: flex;
			gap: 16px;
			overflow-x: auto;
		}
	}
}

@layer promotedbuybox {
	.promoted {
		&.buybox-row {
			display: flex;
			flex-direction: column;
			gap: 8px;
			margin: 0;
		}

		.buybox-row {
			&__label {
				all: unset;
				font-size: 14px;
				font-weight: 500;
				line-height: 16px;
				letter-spacing: 0.21px;
				color: white;
				text-transform: uppercase;

				&__text {
					writing-mode: unset;
				}
			}

			&__offers {
				padding: 0;
			}
		}
	}
}
</style>
